import { useQuery } from "@apollo/client";
import EmptyState from "components/core/EmptyState";
import UserAvatar from "components/User/UserAvatar";
import { CoinsRankingModalV2 } from "components/ui/v2/coins/CoinsRankingModalV2";
import { LiquidPill } from "components/ui/v2";
import { gqlUser } from "gql";
import { IVendorRankingCoinsResponse } from "gql/User/queries";
import { Trophy } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EModalExploreType } from "types/Explorer.types";
import { IUser } from "types/User.types";
import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";
import { UserItemRanking } from "../SideCard/UserItemRanking";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

interface CoinsRankingWidgetProps {
  readonly variant?: "default" | "v2";
}

const CoinsRankingWidget = (props: CoinsRankingWidgetProps) => {
  const { variant = "default" } = props;
  const { t } = useTranslation("explore");
  const [rankingOpen, setRankingOpen] = useState(false);
  const [rankingCoinsData, setRankingCoinsData] = useState<IUser[]>([]);
  const { coinsRanking, setCoinsRanking, hasHydrated } =
    useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(coinsRanking.lastFetched);

  const { loading: coinsLoading } = useQuery<IVendorRankingCoinsResponse>(
    gqlUser.queries.GET_VENDOR_USER_LIST_COIN_SCORE,
    {
      fetchPolicy: "network-only",
      skip: !hasHydrated || hasValidCache,
      variables: {
        vendorUserListInput: {}
      },
      onCompleted: (data) => {
        if (data?.vendorUserList) {
          setCoinsRanking(data.vendorUserList);
        }
      }
    }
  );

  useEffect(() => {
    if (!coinsRanking.data) return;

    let dataCopy = [...coinsRanking.data];

    // * exclude trainers, in user.trainer
    dataCopy = dataCopy.filter((user: IUser) => !user.trainer);

    const dataSorted = dataCopy.sort((a: IUser, b: IUser) => {
      if (
        (a.coinsHistoricallyReceived ?? 0) < (b.coinsHistoricallyReceived ?? 0)
      ) {
        return 1;
      }
      if (
        (a.coinsHistoricallyReceived ?? 0) > (b.coinsHistoricallyReceived ?? 0)
      ) {
        return -1;
      }

      return 0;
    });

    setRankingCoinsData(dataSorted.length ? dataSorted : coinsRanking.data);
  }, [coinsRanking.data]);

  const [activeIndex, setActiveIndex] = useState(0);
  const v2Ranking = useMemo(
    () => rankingCoinsData.slice(0, 5),
    [rankingCoinsData]
  );

  useEffect(() => {
    if (activeIndex > Math.max(v2Ranking.length - 1, 0)) {
      setActiveIndex(0);
    }
  }, [activeIndex, v2Ranking.length]);

  const activeCoinUser = v2Ranking[activeIndex];

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev >= v2Ranking.length - 1 ? 0 : prev + 1));
  }, [v2Ranking.length]);

  useEffect(() => {
    if (variant !== "v2" || v2Ranking.length <= 1) return;

    const interval = window.setInterval(() => {
      goNext();
    }, 4200);

    return () => window.clearInterval(interval);
  }, [goNext, v2Ranking.length, variant]);

  const renderItems = (userRanking: IUser[]) =>
    userRanking.map((r) => (
      <div key={`${r.id}_recognition_card`}>
        <UserItemRanking
          data={r}
          quantity={r.coinsHistoricallyReceived}
          type={EModalExploreType.COINS}
          compact={variant === "v2"}
        />
      </div>
    ));

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  const initialLoading = coinsLoading && !coinsRanking.data;

  if (initialLoading) {
    return (
      <ActiveSideCard
        title={t("Ranking de coins")}
        loading
        defaultExpanded
        variant={variant}
        collapsible={false}
        icon={<Trophy size={16} strokeWidth={1} color="#0073FF" />}
      >
        <span />
      </ActiveSideCard>
    );
  }

  return (
    <>
      <style jsx global>{`
        @keyframes explore-ranking-person-enter {
          0% {
            opacity: 0;
            transform: translateY(14px) scale(0.94);
            filter: blur(8px);
          }
          58% {
            opacity: 1;
            transform: translateY(-2px) scale(1.018);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes explore-ranking-avatar-pop {
          0% {
            opacity: 0;
            transform: scale(0.72) rotate(-4deg);
          }
          68% {
            opacity: 1;
            transform: scale(1.07) rotate(1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes explore-ranking-copy-rise {
          0% {
            opacity: 0;
            transform: translateY(7px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .explore-ranking-person-enter {
          animation: explore-ranking-person-enter 560ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity, filter;
        }
        .explore-ranking-person-enter .explore-ranking-avatar-pop {
          animation: explore-ranking-avatar-pop 620ms
            cubic-bezier(0.34, 1.56, 0.64, 1) 70ms both;
        }
        .explore-ranking-person-enter .explore-ranking-copy-rise {
          animation: explore-ranking-copy-rise 420ms
            cubic-bezier(0.22, 1, 0.36, 1) 150ms both;
        }
      `}</style>
      {(rankingCoinsData?.length ?? 0) > 0 && (
        <ActiveSideCard
          title={t("Ranking de coins")}
          buttonText={(rankingCoinsData?.length ?? 0) > 5 ? t("Ver todos") : ""}
          onViewMore={() => setRankingOpen(true)}
          defaultExpanded
          variant={variant}
          collapsible={false}
          icon={<Trophy size={16} strokeWidth={1} color="#0073FF" />}
        >
          {rankingCoinsData?.length === 0 && (
            <EmptyState
              title={t("Aun no hay usuarios con coins")}
              text={t(
                "Acá podrás ver las coins obtenidas por cada usuario otorgados"
              )}
            />
          )}

          {(rankingCoinsData?.length ?? 0) && variant === "default" && (
            <div>
              <div className="my-2">
                {rankingCoinsData?.length &&
                  renderItems(rankingCoinsData.slice(0, 3))}
              </div>
            </div>
          )}
          {(rankingCoinsData?.length ?? 0) && variant === "v2" && (
            <div className="space-y-2.5">
              {activeCoinUser && (
                <div
                  key={activeCoinUser.id}
                  className="explore-ranking-person-enter relative px-3 py-2"
                >
                  <div className="relative flex flex-col items-center text-center">
                    <LiquidPill
                      variant="gold"
                      appearance="tinted"
                      size="xxs"
                      text={`${activeCoinUser.coinsHistoricallyReceived ?? 0}`}
                      className="absolute right-0 top-0"
                    />

                    <div className="explore-ranking-avatar-pop rounded-full ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 dark:ring-zinc-900 dark:ring-offset-zinc-800">
                      <UserAvatar user={activeCoinUser} size={68} />
                    </div>

                    <p className="explore-ranking-copy-rise mt-2 max-w-full break-words px-2 text-[14px] font-semibold leading-tight text-slate-900 dark:text-zinc-100">
                      {[activeCoinUser.name, activeCoinUser.lastname]
                        .filter(Boolean)
                        .join(" ")}
                    </p>

                    <LiquidPill
                      variant="gold"
                      appearance="tinted"
                      size="xs"
                      text={t("Coins")}
                      className="explore-ranking-copy-rise mt-1"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </ActiveSideCard>
      )}
      <CoinsRankingModalV2
        open={rankingOpen}
        onClose={() => setRankingOpen(false)}
        data={rankingCoinsData ?? []}
      />
    </>
  );
};

export default CoinsRankingWidget;
